import type { VideoData } from '@/frontend-types'
import { EASE_OUT_CUBIC, OVERLAY_DURATION } from '@/helpers/timing'
import { useBodyClip } from '@/hooks/useBodyClip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { Variants } from 'motion/react'
import { motion } from 'motion/react'
import type { FC } from 'react'
import { useEffect, useRef } from 'react'
import { Corridor } from '../atoms/Corridor'
import { Video } from '../atoms/Video'
import { bgOverlayVariants, overlayWrapperVariants } from '../molecules/Overlay'

const videoContentVariants: Variants = {
	hidden: {
		opacity: 0,
		scale: 0.9,
		transition: { duration: OVERLAY_DURATION, ease: EASE_OUT_CUBIC },
	},
	visible: {
		opacity: 1,
		scale: 1,
		transition: { duration: OVERLAY_DURATION, ease: EASE_OUT_CUBIC },
	},
}

export type VideoOverlayProps = {
	id: string
	video: VideoData
	open: boolean
	onClose: () => void
}

export const VideoOverlay: FC<VideoOverlayProps> = ({ id, open, video, onClose }) => {
	const videoRef = useRef<HTMLVideoElement>(null)
	useBodyClip(open, `video-overlay-${id}`)

	// Start and stop video when overlay is opened and closed
	useEffect(() => {
		const video = videoRef.current
		if (video) {
			if (open) video.play()
			else video.pause()
		}
	}, [open])

	// Reset video playback position after fully closed
	const resetVideo = () => {
		const video = videoRef.current
		if (video) video.currentTime = 0
	}

	return (
		<motion.div
			id={id}
			className="fixed inset-0 z-100 items-center justify-center hidden"
			role="dialog"
			aria-modal="true"
			aria-labelledby="watch-video watch-video-close"
			variants={overlayWrapperVariants}
			initial="hidden"
			animate={open ? 'visible' : 'hidden'}
			onAnimationComplete={(variant: string) => {
				if (variant === 'hidden') resetVideo()
			}}
		>
			<motion.div
				variants={bgOverlayVariants}
				className="absolute top-0 left-0 z-0 h-full w-full bg-blue-shade-70"
			/>
			<motion.div
				variants={videoContentVariants}
				className="flex flex-col items-center justify-center h-full w-full"
			>
				<Corridor className="flex flex-col">
					<div className="relative">
						<button
							className="absolute top-lg right-lg z-10 flex w-fit cursor-pointer items-center gap-x-md self-end text-button text-white"
							onClick={onClose}
							aria-expanded={open}
							aria-controls="video-overlay"
							id="watch-video-close"
							aria-label="Close video overlay"
						>
							<FontAwesomeIcon icon={['fas', 'close']} size="xl" />
						</button>
						<div className="flex flex-col items-center justify-center h-full w-full">
							<Video ref={videoRef} video={video} controls className="aspect-video" />
						</div>
					</div>
				</Corridor>
			</motion.div>
		</motion.div>
	)
}
